import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { HomeIcon, BuildingIcon, SignalIcon, GlobeIcon, WrenchIcon, LightningIcon } from '../components/Icons';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServicesContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 2rem 4rem;
  background: var(--bg-secondary);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${slideUp} 0.8s ease;
`;

const PageTitle = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  span { color: #F5B800; }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-bottom: 4rem;
`;

const ServiceCard = styled.div`
  background: var(--card-bg);
  padding: 3rem 2.5rem;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  border-top: 4px solid transparent;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
    border-top-color: #F5B800;
  }
`;


const ServiceTitle = styled.h2`
  font-size: 2rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureItem = styled.li`
  font-size: 1.05rem;
  color: var(--text-secondary);
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;

  &:before {
    content: '→';
    position: absolute;
    left: 0;
    color: #F5B800;
    font-weight: bold;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #0E1114 0%, #2C2F33 100%);
  color: white;
  padding: 5rem 3rem;
  border-radius: 4px;
  text-align: center;
  margin-top: 3rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  border-top: 4px solid #F5B800;
`;

const CTATitle = styled.h2`
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  margin-bottom: 1.5rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;
`;

const CTAText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: #F5B800;
  color: var(--text-primary);
  padding: 1.5rem 4rem;
  border-radius: 4px;
  font-weight: 800;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(245, 184, 0, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Space Grotesk', sans-serif;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(245, 184, 0, 0.5);
    background: #2EC4B6;
  }
`;

const Services = () => {
  return (
    <ServicesContainer>
      <Container>
        <PageHeader>
          <PageTitle>Our <span>Services</span></PageTitle>
          <PageSubtitle>Enterprise Solutions for Large-Scale Telecommunications Providers</PageSubtitle>
        </PageHeader>

        <ServicesGrid>
          <ServiceCard>
            <HomeIcon />
            <ServiceTitle>Fiber & Coax Auditing</ServiceTitle>
            <ServiceDescription>
              Comprehensive auditing and validation services for fiber and coaxial network infrastructure serving large-scale telecommunications providers.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>Network infrastructure audits</FeatureItem>
              <FeatureItem>Fiber optic plant inspection & testing</FeatureItem>
              <FeatureItem>Coaxial network validation</FeatureItem>
              <FeatureItem>Performance benchmarking & analysis</FeatureItem>
              <FeatureItem>Compliance verification</FeatureItem>
              <FeatureItem>Detailed audit reports & recommendations</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>

          <ServiceCard>
            <BuildingIcon />
            <ServiceTitle>Headend & Central Office Installations</ServiceTitle>
            <ServiceDescription>
              Specialized engineering and installation services for headend facilities and central office infrastructure serving large-scale telecommunications providers.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>Headend facility design & deployment</FeatureItem>
              <FeatureItem>Central office infrastructure installation</FeatureItem>
              <FeatureItem>Equipment rack installation & configuration</FeatureItem>
              <FeatureItem>Cable management & structured wiring</FeatureItem>
              <FeatureItem>System integration & turn-up</FeatureItem>
              <FeatureItem>Performance testing & validation</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>

          <ServiceCard>
            <SignalIcon />
            <ServiceTitle>DOCSIS Engineering</ServiceTitle>
            <ServiceDescription>
              Expert DOCSIS 3.0/3.1 implementation, testing, and validation services for telecommunications providers and cable operators.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>DOCSIS 3.0/3.1 OFDM/OFDMA deployment</FeatureItem>
              <FeatureItem>CMTS/CPE configuration & testing</FeatureItem>
              <FeatureItem>RF testing & optimization</FeatureItem>
              <FeatureItem>Modem certification testing</FeatureItem>
              <FeatureItem>IPv6 certification & validation</FeatureItem>
              <FeatureItem>Network problem resolution</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>

          <ServiceCard>
            <GlobeIcon />
            <ServiceTitle>XGS-PON Solutions</ServiceTitle>
            <ServiceDescription>
              Advanced 10-Gigabit-capable Passive Optical Network engineering for next-generation fiber deployments.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>XGS-PON system design & deployment</FeatureItem>
              <FeatureItem>Adtran XGS-PON SME services</FeatureItem>
              <FeatureItem>OLT platform configuration</FeatureItem>
              <FeatureItem>CPE device certification</FeatureItem>
              <FeatureItem>Fiber infrastructure testing</FeatureItem>
              <FeatureItem>Performance validation & optimization</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>

          <ServiceCard>
            <WrenchIcon />
            <ServiceTitle>Network Infrastructure</ServiceTitle>
            <ServiceDescription>
              Comprehensive network infrastructure services for large-scale industrial deployments, including design, installation, testing, and ongoing support for telecommunications providers.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>Enterprise network design & architecture</FeatureItem>
              <FeatureItem>Industrial-scale equipment installation & turn-up</FeatureItem>
              <FeatureItem>Protocol compatibility testing</FeatureItem>
              <FeatureItem>Network optimization & enhancement</FeatureItem>
              <FeatureItem>Technical documentation</FeatureItem>
              <FeatureItem>Troubleshooting & support</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>

          <ServiceCard>
            <LightningIcon />
            <ServiceTitle>Quality Assurance & Testing</ServiceTitle>
            <ServiceDescription>
              Rigorous testing and validation services to ensure your network infrastructure meets the highest standards for large-scale deployments.
            </ServiceDescription>
            <ServiceFeatures>
              <FeatureItem>PON QA testing & validation</FeatureItem>
              <FeatureItem>Comprehensive QA testing protocols</FeatureItem>
              <FeatureItem>Equipment certification testing</FeatureItem>
              <FeatureItem>RF spectrum analysis</FeatureItem>
              <FeatureItem>Performance benchmarking</FeatureItem>
              <FeatureItem>Compliance validation</FeatureItem>
              <FeatureItem>Detailed test documentation</FeatureItem>
            </ServiceFeatures>
          </ServiceCard>
        </ServicesGrid>

        <CTASection>
          <CTATitle>Ready to Get Started?</CTATitle>
          <CTAText>
            Contact us today to discuss your large-scale telecommunications infrastructure project. Vital-Link specializes exclusively in enterprise and industrial deployments for telecommunications providers—not residential installations.
          </CTAText>
          <CTAButton to="/contact">Request a Consultation</CTAButton>
        </CTASection>
      </Container>
    </ServicesContainer>
  );
};

export default Services;
